import { PromotionSelectionCriteria } from '../domain/types/graphql'

/**
 * The two selection criteria a storefront can honestly act on today.
 *
 * The schema has four. `QUANTITY_SELECTION` asks the shopper to spread a
 * quantity across skus and `REFUSED` declines the reward outright; neither is
 * built here, and `docs/NOT_PORTED.md` says why. Narrowing the union rather
 * than widening the model is what keeps a card on screen from offering a button
 * that would send a payload nothing knows how to fill.
 */
export type DeferredPromotionCriteria =
  | PromotionSelectionCriteria.CUSTOMER_SELECTION
  | PromotionSelectionCriteria.DEFAULT_GROUP_ID

/** One sku a reward group grants, before names are resolved. */
export interface DeferredPromotionItemInput {
  sku?: string | null
  quantity?: number | null
}

/** One of `customerGets.groups`: the thing `selectedGroupIdentifier` names. */
export interface DeferredPromotionGroupInput {
  identifier?: string | null
  items?: readonly DeferredPromotionItemInput[] | null
}

/**
 * A product as `customerGets.itemsExtra` carries it. This is the *only* place a
 * promotion says what its skus are called: the groups carry skus and nothing
 * else, so without a match here a reward can only be named by its sku.
 */
export interface DeferredPromotionProductInput {
  sku?: string | null
  name?: string | null
  imageURL?: readonly (string | null)[] | null
}

/**
 * One entry of `cart.deferredPromotions`, narrowed to what a card reads.
 *
 * `IDeferredPromotion` satisfies this. It is not used directly for the same
 * reason `CartItemInput` is not `Item`: the full promotion is a 20-field type
 * hanging off two more, and every test would need all of it to assert on a
 * title.
 */
export interface DeferredPromotionInput {
  count?: number | null
  complexConfiguration?: { selectBy?: PromotionSelectionCriteria | null } | null
  promotion?: {
    id?: string | null
    name?: string | null
    description?: string | null
    customerGets?: {
      groups?: readonly DeferredPromotionGroupInput[] | null
      itemsExtra?: readonly DeferredPromotionProductInput[] | null
    } | null
  } | null
}

/** One sku a reward grants, named as well as the promotion allows. */
export interface DeferredPromotionReward {
  sku: string
  /** The catalog name when `itemsExtra` carries it, otherwise the bare sku. */
  name: string
  /** `null` rather than a placeholder: a card with no picture beats a fake one. */
  imageUrl: string | null
  /** `null` when the group did not say how many, which is not the same as one. */
  quantity: number | null
}

/** One reward the shopper can choose, under `CUSTOMER_SELECTION`. */
export interface DeferredPromotionOption {
  /** `selectedGroupIdentifier`, exactly as it must be sent back. */
  identifier: string
  rewards: DeferredPromotionReward[]
  /** What the control says: the rewards, or the group's own identifier. */
  label: string
}

/** A promotion the cart has earned and not yet claimed. */
export interface DeferredPromotionModel {
  /** `promotionUid` for the mutation. A promotion without one is unclaimable. */
  promotionUid: string
  title: string
  /** Omitted when it would only repeat the title. */
  description: string | null
  /** How many times the basket earned it; `null` when the server did not say. */
  count: number | null
  selectBy: DeferredPromotionCriteria
  /** Empty under `DEFAULT_GROUP_ID`: which reward it is is the server's call. */
  options: DeferredPromotionOption[]
}

const trimmed = (text: string | null | undefined): string | null => {
  const value = text?.trim()
  return value ? value : null
}

const isSupported = (
  selectBy: PromotionSelectionCriteria | null | undefined,
): selectBy is DeferredPromotionCriteria =>
  selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION ||
  selectBy === PromotionSelectionCriteria.DEFAULT_GROUP_ID

/**
 * `itemsExtra` indexed by sku. Built once per promotion rather than searched per
 * item: a group of ten skus against a fifty-product `itemsExtra` is otherwise
 * five hundred comparisons for one card.
 *
 * The first entry for a sku wins. A promotion listing the same sku twice is
 * contradicting itself, and the second copy is no more trustworthy than the
 * first.
 */
const productsBySku = (
  products: readonly DeferredPromotionProductInput[] | null | undefined,
): Map<string, DeferredPromotionProductInput> => {
  const bySku = new Map<string, DeferredPromotionProductInput>()
  for (const product of products ?? []) {
    const sku = trimmed(product?.sku)
    if (sku && !bySku.has(sku)) bySku.set(sku, product)
  }
  return bySku
}

const toRewards = (
  items: readonly DeferredPromotionItemInput[] | null | undefined,
  catalog: Map<string, DeferredPromotionProductInput>,
): DeferredPromotionReward[] => {
  const rewards: DeferredPromotionReward[] = []

  for (const item of items ?? []) {
    // A reward line with no sku names nothing the shopper could recognise —
    // a percentage or a cash amount off, which this card does not render.
    const sku = trimmed(item?.sku)
    if (!sku) continue

    const product = catalog.get(sku)
    rewards.push({
      sku,
      name: trimmed(product?.name) ?? sku,
      imageUrl: trimmed(product?.imageURL?.[0]),
      quantity: item?.quantity ?? null,
    })
  }

  return rewards
}

/** "2 × Coca-Cola 600ml + Vaso" — and just the sku when nothing names it. */
const labelFor = (rewards: readonly DeferredPromotionReward[], identifier: string): string => {
  if (rewards.length === 0) return identifier
  return rewards
    .map((reward) =>
      reward.quantity != null && reward.quantity > 1
        ? `${reward.quantity} × ${reward.name}`
        : reward.name,
    )
    .join(' + ')
}

const toOptions = (
  groups: readonly DeferredPromotionGroupInput[] | null | undefined,
  catalog: Map<string, DeferredPromotionProductInput>,
): DeferredPromotionOption[] => {
  const options: DeferredPromotionOption[] = []
  const taken = new Set<string>()

  for (const group of groups ?? []) {
    // The identifier is the whole payload for `CUSTOMER_SELECTION`. A group
    // without one cannot be chosen, and two groups sharing one would render as
    // two controls that send the same thing.
    const identifier = trimmed(group?.identifier)
    if (!identifier || taken.has(identifier)) continue
    taken.add(identifier)

    const rewards = toRewards(group?.items, catalog)
    options.push({ identifier, rewards, label: labelFor(rewards, identifier) })
  }

  return options
}

/**
 * The promotions a cart has earned but not claimed, as cards.
 *
 * Everything dropped here is dropped because the entry could not be *acted on*,
 * not because it looked untidy:
 *
 * - no `promotion.id` — `promotionUid` is the mutation's only required
 *   identifier, so there is nothing to send;
 * - a `selectBy` outside {@link DeferredPromotionCriteria}, including a missing
 *   `complexConfiguration`. `selectBy` is non-null in the mutation input and
 *   guessing one would be proposing a reward the shopper never chose;
 * - no name and no description — a card with no words tells the shopper
 *   nothing, and inventing one ("Promoción disponible") would be putting our
 *   copy in the storefront's mouth;
 * - `CUSTOMER_SELECTION` with no usable group — a choice with nothing to choose
 *   between is a button that cannot be pressed.
 *
 * The order the server sent is kept. Promotions carry a `priority` the backend
 * has already sorted by, and re-ranking them here would be a second opinion.
 */
export const toDeferredPromotionModels = (
  deferredPromotions: readonly DeferredPromotionInput[] | null | undefined,
): DeferredPromotionModel[] => {
  const models: DeferredPromotionModel[] = []
  const taken = new Set<string>()

  for (const deferred of deferredPromotions ?? []) {
    const promotionUid = trimmed(deferred?.promotion?.id)
    if (!promotionUid || taken.has(promotionUid)) continue

    const selectBy = deferred?.complexConfiguration?.selectBy
    if (!isSupported(selectBy)) continue

    const name = trimmed(deferred?.promotion?.name)
    const description = trimmed(deferred?.promotion?.description)
    const title = name ?? description
    if (!title) continue

    const customerGets = deferred?.promotion?.customerGets
    const options =
      selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION
        ? toOptions(customerGets?.groups, productsBySku(customerGets?.itemsExtra))
        : []
    if (selectBy === PromotionSelectionCriteria.CUSTOMER_SELECTION && options.length === 0) continue

    taken.add(promotionUid)
    models.push({
      promotionUid,
      title,
      description: description === title ? null : description,
      count: deferred?.count ?? null,
      selectBy,
      options,
    })
  }

  return models
}
